import { useEffect, useState } from 'react';
import {
  Alert, Button, Chip, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel,
  FormLabel, Radio, RadioGroup, Stack, Switch, Table, TableBody, TableCell, TableHead,
  TableRow, TextField, Typography,
} from '@mui/material';
import { Navigate, useRouteLoaderData } from 'react-router';
import toast from 'react-hot-toast';
import { GlassCard } from '@/components/GlassCard';
import { GlassDialog } from '@/components/GlassDialog';
import { apiErrorMessage } from '@/lib/errors';
import type { UserProfile } from '@/auth/session';
import { createRootUser, listRootUsers } from '@/features/rootUsers/api';
import type { RootUser, RootUserRole } from '@/features/rootUsers/api';

/// Rendered instead of the raw role so the table reads in Lao — and so a user
/// literally named "admin" does not produce two cells with identical text.
const ROLE_LABEL: Record<RootUserRole, string> = { admin: 'ແອດມິນ', user: 'ຜູ້ໃຊ້' };

export default function RootUsersPage() {
  const user = useRouteLoaderData('protected') as UserProfile | undefined;

  const [users, setUsers] = useState<RootUser[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<RootUserRole>('user');
  const [state, setState] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let active = true;
    async function load() {
      setError(null);
      try {
        const res = await listRootUsers();
        if (active) setUsers(res);
      } catch (e) {
        if (active) setError(apiErrorMessage(e, 'ບໍ່ສາມາດໂຫຼດລາຍການຜູ້ໃຊ້ໄດ້'));
      }
    }
    void load();
    return () => { active = false; };
  }, [reloadKey]);

  // UX only. /api/root-users sits behind require_admin, which re-reads the
  // role from m_user_root on every request — this just avoids showing a page
  // that would only 403.
  if (user && user.role !== 'admin') return <Navigate to="/" replace />;

  function reset() { setUsername(''); setPassword(''); setRole('user'); setState(true); }

  async function save() {
    setSaving(true);
    try {
      await toast.promise(
        createRootUser({ username: username.trim(), password, role, state }),
        {
          loading: 'ກຳລັງສ້າງຜູ້ໃຊ້...', success: 'ສ້າງຜູ້ໃຊ້ສຳເລັດ',
          error: (e) => apiErrorMessage(e, 'ສ້າງຜູ້ໃຊ້ບໍ່ສຳເລັດ'),
        },
      );
      // The plaintext password is never kept once it has been sent.
      reset();
      setOpen(false);
      setReloadKey((k) => k + 1);
    } catch {
      // toast.promise already displayed the error toast. Leave the dialog
      // open on a name clash so the operator can just retype the name
      // instead of refilling the whole form.
    } finally {
      setSaving(false);
    }
  }

  return (
    <Stack spacing={3}>
      <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
        <Typography variant="h3">ຜູ້ໃຊ້ລະບົບ</Typography>
        <Button variant="contained" onClick={() => setOpen(true)}>ເພີ່ມຜູ້ໃຊ້</Button>
      </Stack>

      <GlassCard>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ຊື່ຜູ້ໃຊ້</TableCell>
              <TableCell>ສິດ</TableCell>
              <TableCell>ສະຖານະ</TableCell>
              <TableCell>ວັນທີສ້າງ</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u.id}>
                <TableCell>{u.username}</TableCell>
                <TableCell>{ROLE_LABEL[u.role]}</TableCell>
                <TableCell>
                  <Chip size="small" label={u.state ? 'ໃຊ້ງານ' : 'ປິດ'} color={u.state ? 'success' : 'default'} />
                </TableCell>
                <TableCell>{u.createdAt.slice(0, 10)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </GlassCard>

      <GlassDialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>ເພີ່ມຜູ້ໃຊ້</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField label="ຊື່ຜູ້ໃຊ້" value={username} required fullWidth
              onChange={(e) => setUsername(e.target.value)} />
            <TextField label="ລະຫັດຜ່ານ" type="password" value={password} required fullWidth
              onChange={(e) => setPassword(e.target.value)} />
            <FormControl>
              <FormLabel>ສິດ</FormLabel>
              <RadioGroup row value={role} onChange={(e) => setRole(e.target.value as RootUserRole)}>
                <FormControlLabel value="user" control={<Radio />} label="ຜູ້ໃຊ້" />
                <FormControlLabel value="admin" control={<Radio />} label="ແອດມິນ" />
              </RadioGroup>
            </FormControl>
            <FormControlLabel
              control={<Switch checked={state} onChange={(e) => setState(e.target.checked)} />}
              label="ໃຊ້ງານໄດ້"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} color="inherit">ຍົກເລີກ</Button>
          <Button variant="contained" loading={saving}
            disabled={!username.trim() || !password.trim()}
            onClick={() => { void save(); }}>
            ບັນທຶກ
          </Button>
        </DialogActions>
      </GlassDialog>
    </Stack>
  );
}
